import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  StyledContentTutorial,
  IconWrapper,
  SearchContainer,
  SeparatorLine,
  IconFlipWrapper,
  ToolbarTutorial,
  NoTutorials,
} from './styled/TutorialVideos.styled';

import IconButton from '../../juristec-ui/core/IconButton';
import InputTextLine from '../../juristec-ui/core/InputTextLine';
import Tooltip from '../../juristec-ui/core/Tooltip';

import {
  Finder, Play, Edit, Trash, ExpandMore, Add, Chain,
} from '../../juristec-ui/icons';

import registerNamespace from './locales/localesTutorialVideos';

const namespace = registerNamespace('src/components/Header/TutorialVideos.tsx');

interface Tutorial {
  tutorialId: string;
  title: string;
  description: string;
  group: string;
  url: string;
}

const tutorialVideosDefaultPropTutorialsList: Tutorial[] = [];

interface TutorialVideosProps {
  tutorialsList: Tutorial[];
  userIsAdmin?: boolean;
  playVideo: (url: string) => void;
  setVideoDescription: (description: string) => void;
  addTutorial: () => void;
  copyTutorialUrl: (tutorialId: string) => void;
  editTutorial: (tutorialId: string) => void;
  removeTutorial: (tutorialId: string) => void;
}

const TutorialVideos = ({
  tutorialsList = tutorialVideosDefaultPropTutorialsList,
  userIsAdmin = false,
  playVideo,
  setVideoDescription,
  addTutorial,
  copyTutorialUrl,
  editTutorial,
  removeTutorial,
}: TutorialVideosProps) => {
  const [searchText, setSearchText] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const { t } = useTranslation(namespace);

  const theme = useTheme() as Record<string, string>;

  const toggleGroup = (group: string) => {
    setExpandedGroups((current) => {
      const next = new Set(current);

      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }

      return next;
    });
  };

  const search = searchText.toLowerCase();

  const groupedTutorials = Object.entries(
    tutorialsList
      .filter((tutorial) => (
        tutorial.title?.toLowerCase().includes(search)
        || tutorial.description?.toLowerCase().includes(search)
        || tutorial.group?.toLowerCase().includes(search)
      ))
      .reduce((groups, tutorial) => {
        const group = tutorial.group || 'Sem Grupo';

        if (!groups[group]) {
          groups[group] = [];
        }

        groups[group].push(tutorial);

        return groups;
      }, {} as Record<string, Tutorial[]>),
  )
    .sort(([groupA], [groupB]) => groupA.localeCompare(groupB))
    .map(([group, tutorials]) => ({
      group,
      tutorials: tutorials.sort((a, b) => a.title.localeCompare(b.title)),
    }));

  return (
    <StyledContentTutorial>
      <h1>{t('tutorials')}</h1>
      <SeparatorLine />
      <ToolbarTutorial>
        <SearchContainer>
          <IconWrapper>
            <Finder />
          </IconWrapper>
          <InputTextLine
            placeholder={t('search')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ color: theme.color }}
            setValue={() => {}}
          />
        </SearchContainer>
        {userIsAdmin && (
          <Tooltip text={t('addTutorial')}>
            <IconButton
              color="secondary"
              variant="contained"
              shape="rounded"
              onClick={addTutorial}
            >
              <Add />
            </IconButton>
          </Tooltip>
        )}
      </ToolbarTutorial>
      {tutorialsList?.length > 0 ? (
        <ul className="list">
          {groupedTutorials.map(({ group, tutorials }) => {
            const isExpanded = expandedGroups.has(group);

            return (
              <li key={group} className="tutorial-group">
                <button
                  type="button"
                  className="group-header"
                  onClick={() => toggleGroup(group)}
                >
                  <IconFlipWrapper flip={isExpanded}>
                    <ExpandMore />
                  </IconFlipWrapper>

                  <span className="group-title">
                    {group}
                  </span>

                  <span className="group-count">
                    {tutorials.length}
                  </span>
                </button>

                {isExpanded && (
                  <ul className="group-tutorials">
                    {tutorials.map((item) => (
                      <li
                        key={item.tutorialId}
                        className="item-list"
                        onClick={() => {
                          playVideo(item.url);
                          setVideoDescription(item.description);
                        }}
                        onKeyDown={() => {
                          playVideo(item.url);
                          setVideoDescription(item.description);
                        }}
                        role="menuitem"
                      >
                        <div className="item-content">
                          <IconButton color="secondary" variant="contained" shape="circle">
                            <Play />
                          </IconButton>
                          <span>
                            {item.title}
                          </span>
                        </div>

                        <div className="item-actions">
                          <Tooltip text={t('copyTutorialLink')}>
                            <IconButton
                              // @ts-expect-error not typed at IconButton
                              onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                event.stopPropagation();
                                copyTutorialUrl(item.tutorialId);
                              }}
                            >
                              <Chain />
                            </IconButton>
                          </Tooltip>
                          {userIsAdmin && (
                            <>
                              <Tooltip text={t('updateTutorial')}>
                                <IconButton
                                  // @ts-expect-error not typed at IconButton
                                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                    event.stopPropagation();
                                    editTutorial(item.tutorialId);
                                  }}
                                >
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip text={t('deleteTutorial')}>
                                <IconButton
                                  // @ts-expect-error not typed at IconButton
                                  onClick={(event: MouseEvent<HTMLButtonElement>) => {
                                    event.stopPropagation();
                                    removeTutorial(item.tutorialId);
                                  }}
                                >
                                  <Trash />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <NoTutorials>
          {t('noTutorialsRegistered')}
        </NoTutorials>
      )}
    </StyledContentTutorial>
  );
};

export default TutorialVideos;
